<script setup>
/* global axios */
// Aba "Flows" das configurações do inbox de WhatsApp oficial (Cloud API).
// Lista os WhatsApp Flows (Meta) do WABA e, pelo builder, cria formulários (nível B):
// o usuário monta os campos, o backend gera o Flow JSON e cria um RASCUNHO na Meta;
// publicar/excluir por linha. Flow publicado é referenciável no botão FLOW de um template.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/whatsapp_flows`;
const inboxId = computed(() => props.inbox.id);

const flows = ref([]);
const loading = ref(true);
const error = ref('');
const search = ref('');
const busyId = ref(null);

const STATUS_CLASS = {
  PUBLISHED: 'bg-n-teal-3 text-n-teal-11',
  DRAFT: 'bg-n-amber-3 text-n-amber-11',
  DEPRECATED: 'bg-n-alpha-2 text-n-slate-11',
  BLOCKED: 'bg-n-ruby-3 text-n-ruby-11',
  THROTTLED: 'bg-n-ruby-3 text-n-ruby-11',
};

const CATEGORIES = [
  'SIGN_UP',
  'SIGN_IN',
  'APPOINTMENT_BOOKING',
  'LEAD_GENERATION',
  'CONTACT_US',
  'CUSTOMER_SUPPORT',
  'SURVEY',
  'OTHER',
];
const FIELD_TYPES = [
  'heading',
  'body',
  'text',
  'email',
  'phone',
  'number',
  'textarea',
  'dropdown',
  'radio',
  'checkbox',
  'date',
  'optin',
];
const NEEDS_OPTIONS = ['dropdown', 'radio', 'checkbox'];
const DISPLAY_ONLY = ['heading', 'body'];

const filtered = computed(() =>
  flows.value.filter(
    f =>
      !search.value ||
      (f.name || '').toLowerCase().includes(search.value.toLowerCase())
  )
);

const loadFlows = async () => {
  loading.value = true;
  error.value = '';
  try {
    flows.value =
      (await axios.get(`${base}?inbox_id=${inboxId.value}`)).data.data || [];
  } catch (e) {
    error.value =
      e?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_FLOWS.LOAD_ERROR');
    flows.value = [];
  } finally {
    loading.value = false;
  }
};

// ---- builder ----
const showBuilder = ref(false);
const saving = ref(false);
const builderError = ref('');
const validationErrors = ref([]);
const editingId = ref(null);
const freshForm = () => ({
  name: '',
  category: 'OTHER',
  screen_title: '',
  submit_label: 'Enviar',
  fields: [{ type: 'text', label: '', required: true, optionsText: '' }],
});
const form = ref(freshForm());

const openBuilder = () => {
  editingId.value = null;
  form.value = freshForm();
  builderError.value = '';
  validationErrors.value = [];
  showBuilder.value = true;
};

// Carrega um rascunho de volta no builder (edição in-place).
const editFlow = async f => {
  busyId.value = f.id;
  builderError.value = '';
  validationErrors.value = [];
  try {
    const spec = (await axios.get(`${base}/${f.id}?inbox_id=${inboxId.value}`))
      .data.data;
    form.value = {
      name: spec.name || f.name || '',
      category: spec.category || 'OTHER',
      screen_title: spec.screen_title || '',
      submit_label: spec.submit_label || 'Enviar',
      fields: (spec.fields || []).map(fl => ({
        type: fl.type,
        label: fl.label || '',
        required: fl.required === true,
        optionsText: (fl.options || []).join(', '),
      })),
    };
    if (!form.value.fields.length)
      form.value.fields = [
        { type: 'text', label: '', required: true, optionsText: '' },
      ];
    editingId.value = f.id;
    showBuilder.value = true;
  } catch (e) {
    window.alert(
      e?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_FLOWS.EDIT_ERROR')
    );
  } finally {
    busyId.value = null;
  }
};
const addField = () =>
  form.value.fields.push({
    type: 'text',
    label: '',
    required: false,
    optionsText: '',
  });
const removeField = i => form.value.fields.splice(i, 1);
const moveField = (i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= form.value.fields.length) return;
  const f = form.value.fields;
  [f[i], f[j]] = [f[j], f[i]];
};

const saveFlow = async () => {
  builderError.value = '';
  validationErrors.value = [];
  if (!form.value.name.trim() || !form.value.screen_title.trim()) {
    builderError.value = t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.REQUIRED');
    return;
  }
  saving.value = true;
  try {
    const fields = form.value.fields.map(f => ({
      type: f.type,
      label: f.label,
      required: f.required,
      options: NEEDS_OPTIONS.includes(f.type)
        ? (f.optionsText || '')
            .split(/[\n,]/)
            .map(o => o.trim())
            .filter(Boolean)
        : undefined,
    }));
    const payload = {
      inbox_id: inboxId.value,
      name: form.value.name,
      category: form.value.category,
      screen_title: form.value.screen_title,
      submit_label: form.value.submit_label,
      fields,
    };
    const res = editingId.value
      ? await axios.put(`${base}/${editingId.value}`, payload)
      : await axios.post(base, payload);
    validationErrors.value = res.data?.data?.validation_errors || [];
    if (validationErrors.value.length) {
      // criado como rascunho, mas com avisos da Meta
      await loadFlows();
    } else {
      showBuilder.value = false;
      await loadFlows();
    }
  } catch (e) {
    builderError.value =
      e?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.ERROR');
  } finally {
    saving.value = false;
  }
};

const publishFlow = async f => {
  if (
    !window.confirm(
      t('INBOX_MGMT.WHATSAPP_FLOWS.CONFIRM_PUBLISH', { name: f.name })
    )
  )
    return;
  busyId.value = f.id;
  try {
    await axios.post(`${base}/${f.id}/publish`, { inbox_id: inboxId.value });
    await loadFlows();
  } catch (e) {
    window.alert(
      e?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_FLOWS.PUBLISH_ERROR')
    );
  } finally {
    busyId.value = null;
  }
};
const deleteFlow = async f => {
  if (
    !window.confirm(
      t('INBOX_MGMT.WHATSAPP_FLOWS.CONFIRM_DELETE', { name: f.name })
    )
  )
    return;
  busyId.value = f.id;
  try {
    await axios.delete(`${base}/${f.id}`, {
      data: { inbox_id: inboxId.value },
    });
    await loadFlows();
  } catch (e) {
    window.alert(
      e?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_FLOWS.DELETE_ERROR')
    );
  } finally {
    busyId.value = null;
  }
};

onMounted(loadFlows);
</script>

<template>
  <div class="py-4">
    <div class="flex items-start justify-between gap-4 mb-1">
      <div>
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_FLOWS.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.WHATSAPP_FLOWS.SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-3 whitespace-nowrap">
        <button
          class="text-sm font-medium text-n-brand hover:underline"
          :disabled="loading"
          @click="loadFlows"
        >
          {{ t('INBOX_MGMT.WHATSAPP_FLOWS.REFRESH') }}
        </button>
        <NextButton
          sm
          :label="t('INBOX_MGMT.WHATSAPP_FLOWS.NEW')"
          @click="openBuilder"
        />
      </div>
    </div>

    <input
      v-model="search"
      :placeholder="t('INBOX_MGMT.WHATSAPP_FLOWS.SEARCH')"
      class="w-full max-w-xs px-3 py-2 mt-3 mb-4 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
    />

    <p v-if="error" class="mb-4 text-sm text-n-ruby-11">{{ error }}</p>

    <p v-if="loading" class="py-10 text-sm text-center text-n-slate-11">
      {{ t('INBOX_MGMT.WHATSAPP_FLOWS.LOADING') }}
    </p>

    <p
      v-else-if="!filtered.length"
      class="py-10 text-sm text-center text-n-slate-11"
    >
      {{ t('INBOX_MGMT.WHATSAPP_FLOWS.EMPTY') }}
    </p>

    <div v-else class="overflow-hidden border rounded-xl border-n-weak">
      <table class="w-full text-sm">
        <thead class="bg-n-alpha-1 text-n-slate-11">
          <tr>
            <th class="px-4 py-2 font-medium text-left">
              {{ t('INBOX_MGMT.WHATSAPP_FLOWS.COL_NAME') }}
            </th>
            <th class="px-4 py-2 font-medium text-left">
              {{ t('INBOX_MGMT.WHATSAPP_FLOWS.COL_STATUS') }}
            </th>
            <th class="px-4 py-2 font-medium text-left">
              {{ t('INBOX_MGMT.WHATSAPP_FLOWS.COL_CATEGORIES') }}
            </th>
            <th class="px-4 py-2 font-medium text-left">
              {{ t('INBOX_MGMT.WHATSAPP_FLOWS.COL_ID') }}
            </th>
            <th class="px-4 py-2 font-medium text-right">
              {{ t('INBOX_MGMT.WHATSAPP_FLOWS.COL_ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="f in filtered"
            :key="f.id"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="px-4 py-2 font-medium">{{ f.name }}</td>
            <td class="px-4 py-2">
              <span
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  STATUS_CLASS[f.status] || 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ f.status }}
              </span>
            </td>
            <td class="px-4 py-2 text-n-slate-11">
              {{ (f.categories || []).join(', ') || '—' }}
            </td>
            <td class="px-4 py-2 font-mono text-xs text-n-slate-10">
              {{ f.id }}
            </td>
            <td class="px-4 py-2 text-right whitespace-nowrap">
              <button
                v-if="f.status === 'DRAFT'"
                class="text-xs font-medium text-n-brand hover:underline disabled:opacity-50"
                :disabled="busyId === f.id"
                @click="editFlow(f)"
              >
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.EDIT') }}
              </button>
              <button
                v-if="f.status === 'DRAFT'"
                class="ml-3 text-xs font-medium text-n-teal-11 hover:underline disabled:opacity-50"
                :disabled="busyId === f.id"
                @click="publishFlow(f)"
              >
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.PUBLISH') }}
              </button>
              <button
                v-if="f.status === 'DRAFT'"
                class="ml-3 text-xs font-medium text-n-ruby-11 hover:underline disabled:opacity-50"
                :disabled="busyId === f.id"
                @click="deleteFlow(f)"
              >
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.DELETE') }}
              </button>
              <span v-else class="text-xs text-n-slate-9">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="mt-4 text-xs text-n-slate-10">
      {{ t('INBOX_MGMT.WHATSAPP_FLOWS.FOOTER_HINT') }}
    </p>

    <!-- Builder -->
    <div
      v-if="showBuilder"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      @click.self="showBuilder = false"
    >
      <div
        class="w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-n-background rounded-xl shadow-xl"
      >
        <div
          class="flex items-center justify-between px-5 py-4 border-b border-n-weak"
        >
          <h4 class="text-base font-medium text-n-slate-12">
            {{
              editingId
                ? t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.TITLE_EDIT')
                : t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.TITLE')
            }}
          </h4>
          <button
            class="p-1 rounded text-n-slate-11 hover:bg-n-alpha-2"
            @click="showBuilder = false"
          >
            <svg
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="px-5 py-4 space-y-4">
          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label class="block mb-1 text-xs text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.NAME') }}
              </label>
              <input
                v-model="form.name"
                :placeholder="t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.NAME_PH')"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-xs text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.CATEGORY') }}
              </label>
              <select
                v-model="form.category"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              >
                <option v-for="c in CATEGORIES" :key="c" :value="c">
                  {{ c }}
                </option>
              </select>
            </div>
            <div>
              <label class="block mb-1 text-xs text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.SCREEN_TITLE') }}
              </label>
              <input
                v-model="form.screen_title"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.SCREEN_TITLE_PH')
                "
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-xs text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.SUBMIT_LABEL') }}
              </label>
              <input
                v-model="form.submit_label"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.FIELDS') }}
              </label>
              <button
                class="text-xs font-medium text-n-brand hover:underline"
                @click="addField"
              >
                {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.ADD_FIELD') }}
              </button>
            </div>
            <div
              v-for="(fld, i) in form.fields"
              :key="i"
              class="p-3 mb-2 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <div class="flex flex-wrap items-center gap-2">
                <select
                  v-model="fld.type"
                  class="pl-2 pr-8 py-1.5 text-xs border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                >
                  <option v-for="ft in FIELD_TYPES" :key="ft" :value="ft">
                    {{
                      t(`INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.FIELD_TYPE.${ft}`)
                    }}
                  </option>
                </select>
                <input
                  v-model="fld.label"
                  :placeholder="
                    DISPLAY_ONLY.includes(fld.type)
                      ? t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.TEXT_PH')
                      : t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.LABEL_PH')
                  "
                  class="flex-1 px-2 py-1.5 text-xs border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                />
                <label
                  v-if="!DISPLAY_ONLY.includes(fld.type)"
                  class="flex items-center gap-1 text-xs text-n-slate-11"
                >
                  <input v-model="fld.required" type="checkbox" />
                  {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.REQUIRED_FIELD') }}
                </label>
                <button
                  :aria-label="t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.MOVE_UP')"
                  class="p-1 rounded text-n-slate-10 hover:bg-n-alpha-2"
                  @click="moveField(i, -1)"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M18 15l-6-6-6 6"
                    />
                  </svg>
                </button>
                <button
                  :aria-label="t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.MOVE_DOWN')"
                  class="p-1 rounded text-n-slate-10 hover:bg-n-alpha-2"
                  @click="moveField(i, 1)"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M6 9l6 6 6-6"
                    />
                  </svg>
                </button>
                <button
                  class="p-1 rounded text-n-ruby-11 hover:bg-n-ruby-3"
                  @click="removeField(i)"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <input
                v-if="NEEDS_OPTIONS.includes(fld.type)"
                v-model="fld.optionsText"
                :placeholder="t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.OPTIONS_PH')"
                class="w-full mt-2 px-2 py-1.5 text-xs border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>
          </div>

          <p v-if="builderError" class="text-sm text-n-ruby-11">
            {{ builderError }}
          </p>
          <div
            v-if="validationErrors.length"
            class="p-3 text-xs border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
          >
            <p class="mb-1 font-medium">
              {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.VALIDATION') }}
            </p>
            <ul class="list-disc list-inside">
              <li v-for="(ve, vi) in validationErrors" :key="vi">
                {{ ve.error_type || ve.error }} — {{ ve.message }}
              </li>
            </ul>
          </div>
        </div>

        <div
          class="flex items-center justify-end gap-3 px-5 py-4 border-t border-n-weak"
        >
          <button
            class="text-sm font-medium text-n-slate-11 hover:underline"
            @click="showBuilder = false"
          >
            {{ t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.CANCEL') }}
          </button>
          <NextButton
            :label="
              editingId
                ? t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.SAVE_EDIT')
                : t('INBOX_MGMT.WHATSAPP_FLOWS.BUILDER.SAVE')
            "
            :is-loading="saving"
            :disabled="saving"
            @click="saveFlow"
          />
        </div>
      </div>
    </div>
  </div>
</template>
